import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import {
  filter, each, groupBy, cloneDeep, reduce, sumBy, map,
} from 'lodash';
import ReactHighcharts from 'react-highcharts';
import { Dropdown, MenuItem } from 'react-bootstrap';
import accounting from 'accounting';
import numeral from 'numeral';
import { format as formatMoney } from '../../../../components/money-display';
import ForecastForm from '../forecast-form';
import { getColorFromKey, getNameFromKey } from '../../../../util/helpers';
import { getHasNoDecisionSetup } from '../../../../components/search-form/search-form-reducer';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import ProgressBar from '../../../../components/progress-bar';
import { getIsFontSizeBig } from '../../../App/AppReducer';

// Import Selectors
import { getOutcome } from '../../PerformanceReducer';
import { fetchForecast } from '../../PerformanceActions';


function createCategory(size, has100Plus = false) {
  const categories = [];
  if (size > 0) {
    const max = 100;
    let c = 0;
    while (c < max) {
      const next = Math.min(c + size, max);
      categories.push({
        label: `${accounting.formatNumber(c, 0)} - ${accounting.formatNumber(next, 0)}%`,
        altLabel: `between ${accounting.formatNumber(c, 0)} - ${accounting.formatNumber(next, 0)}%`,
        min: c,
        max: next,
      });
      c = next;
    }
    if (has100Plus) {
      categories.push({
        label: '100% +',
        altLabel: 'above 100%',
        min: 100,
        max: 1000,
      });
    }
  }
  return categories;
}

function bucketize(unqualifiedStats, forecastKey) {
  const stats = filter(unqualifiedStats, (stat) => {
    const { owner } = stat;
    return owner && owner.is_active;
  });
  const keys = ['won', 'lost', 'noDecision', 'forecast'];
  const results = { items: [], has100Plus: false };
  // default values
  each(keys, (key) => {
    results[key] = {
      mean: { amount: 0, count: 0 },
      median: { amount: 0, count: 0 },
      deviation: { amount: 0, count: 0 },
      bucketSize: { amount: 0, count: 0 },
    };
  });

  const total = stats.length;
  if (total > 0) {
    const totals = {};
    const alls = {};
    each(stats, (stat) => {
      each(keys, (key) => {
        if (!totals[key]) {
          totals[key] = { amount: 0, count: 0 };
        }
        if (!alls[key]) {
          alls[key] = { amount: [], count: [] };
        }
        if (key === 'forecast') {
          const fc = (stat[forecastKey] || 0) * 100;
          alls[key].amount.push(fc);
          alls[key].count.push(fc);
          totals[key].amount += fc;
          totals[key].count += fc;
        } else {
          alls[key].amount.push(stat.stages[key].amountPercentage);
          alls[key].count.push(stat.stages[key].countPercentage);
          totals[key].amount += stat.stages[key].amountPercentage;
          totals[key].count += stat.stages[key].countPercentage;
        }
      });
    });

    each(keys, (key) => {
      each(['amount', 'count'], (type) => {
        alls[key][type] = alls[key][type].sort((a, b) => (a - b > 0 ? 1 : -1));
        results[key].mean[type] = totals[key][type] / total;
        results[key].median[type] = alls[key][type][Math.floor(alls[key].amount.length / 2)];
        results[key].deviation[type] = Math.sqrt(reduce(alls[key][type], (s, item) => s + ((item - results[key].mean[type]) ** 2), 0) / total);
        results[key].bucketSize[type] = Math.floor(results[key].deviation[type] / 2);
      });
    });
    each(stats, (stat) => {
      const cloneStat = cloneDeep(stat);
      each(keys, (key) => {
        if (key === 'forecast') {
          const fc = (stat[forecastKey] || 0) * 100;
          if (fc > 100) {
            results.has100Plus = true;
          }
          // cloneStat.forecastBucket = results[key].bucketSize.amount !== 0 ? Math.floor(fc / results[key].bucketSize.amount) : 0;
          // https://github.com/lumen-software/TruSales/issues/730
          // the axis depend on WON buckets!!!
          cloneStat.forecastBucketAmount = results.won.bucketSize.amount !== 0 ? Math.floor(fc / results.won.bucketSize.amount) : 0;
          cloneStat.forecastBucketCount = results.won.bucketSize.count !== 0 ? Math.floor(fc / results.won.bucketSize.count) : 0;
        } else {
          // cloneStat.stages[key].amountBucket = results[key].bucketSize.amount !== 0 ? Math.floor(stat.stages[key].amountPercentage / results[key].bucketSize.amount) : 0;
          // cloneStat.stages[key].countBucket = results[key].bucketSize.count !== 0 ? Math.floor(stat.stages[key].countPercentage / results[key].bucketSize.count) : 0;
          // https://github.com/lumen-software/TruSales/issues/730
          // the axis depend on WON buckets!!!
          cloneStat.stages[key].amountBucket = results.won.bucketSize.amount !== 0 ? Math.floor(stat.stages[key].amountPercentage / results.won.bucketSize.amount) : 0;
          cloneStat.stages[key].countBucket = results.won.bucketSize.count !== 0 ? Math.floor(stat.stages[key].countPercentage / results.won.bucketSize.count) : 0;
        }
      });
      results.items.push(cloneStat);
    });
  }

  return results;
}

const USER_PREFERENCE_REPORT_TYPE = 'performance-distribution-report-type';
const USER_PREFERENCE_CHART_TYPE = 'performance-distribution-chart-type';
const USER_PREFERENCE_FORECAST_CATEGORY = 'performance-distribution-forecast-category';
const USER_PREFERENCE_PERIOD = 'performance-distribution-forecast-period';

class DistributionGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      type: 'amount',
      chartType: 'line',
      buckets: null,
      chartCategories: [],
      // loadingForecast: props.displayForecastData, // init,
      loadingForecast: false,
      period: null,
      forecastCategoryParams: null,
    };
    this.setChartType = this.setChartType.bind(this);
    this.setReportType = this.setReportType.bind(this);
    this.handleForecastParamChange = this.handleForecastParamChange.bind(this);
  }

  componentWillMount() {
    const { userPreferences } = this.context;
    const type = userPreferences[USER_PREFERENCE_REPORT_TYPE] || 'amount';
    const chartType = userPreferences[USER_PREFERENCE_CHART_TYPE] || 'line';
    this.setState({
      type, chartType,
    });
    this.checkState(this.props.outcome);
  }

  componentWillReceiveProps(nextProps) {
    this.checkState(nextProps.outcome);
  }

  setChartType(chartType) {
    this.setState({ chartType });
    this.context.updateUserPreference(USER_PREFERENCE_CHART_TYPE, chartType);
  }

  setReportType(type) {
    this.setState({ type });
    this.context.updateUserPreference(USER_PREFERENCE_REPORT_TYPE, type);
  }

  checkState(outcome) {
    const forecastKey = `${this.props.forecastKey}Forecast`;
    const buckets = bucketize(outcome, forecastKey);
    const { type } = this.state;
    if (buckets && buckets.items.length > 0) {
      const chartCategories = createCategory(buckets.won.bucketSize[type], buckets.has100Plus);
      this.setState({ chartCategories });
    }
    this.setState({ buckets });
  }

  handleForecastParamChange(period, forecastCategoryParams) {
    this.setState({ period, forecastCategoryParams }); // remember this, for chart segment click events
    const { criteria, minForecastDate, maxForecastDate } = this.props;
    const params = {
      ...criteria,
      selectedPeriod: period,
      forecast: true,
      period: [minForecastDate, maxForecastDate],
    };
    this.setState({ loadingForecast: true });
    this.props.dispatch(fetchForecast(params, forecastCategoryParams, 'graph'))
      .then(
        () => {
          this.setState({ loadingForecast: false });
        },
        () => {
          this.setState({ loadingForecast: false });
        },
      );
  }

  renderGraph() {
    const { hasNoDecisionSetup, organization, isFontSizeBig } = this.props;
    const { buckets, chartCategories } = this.state;
    const forecastKey = `${this.props.forecastKey}Forecast`;
    if (!buckets || buckets.items.length === 0) {
      return null;
    }
    if (!buckets.won || !buckets.won.bucketSize || !buckets.won.bucketSize.count) {
      return null;
    }

    const { type, chartType } = this.state;
    const categories = chartCategories;

    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    const series = [];
    const keys = ['won', 'lost'];
    if (hasNoDecisionSetup) {
      keys.push('noDecision');
    }
    keys.push('forecast');

    each(keys, (key) => {
      const isForecast = key === 'forecast';

      const groupedData = groupBy(buckets.items, (item) => {
        if (key === 'forecast') {
          // const fc = (item.graphForecast || 0) * 100;
          // return findIndex(categories, category => (category.min <= fc && (fc < category.max || fc === 100)));
          return type === 'count' ? item.forecastBucketCount : item.forecastBucketAmount;
        } else if (item.stages[key].total) {
          return item.stages[key][`${type}Bucket`];
        }
        return null;
      });
      const data = [];
      each(categories, (category, k) => {
        if (groupedData[k]) {
          const y = groupedData[k].length || 0;
          const count = sumBy(groupedData[k], 'total');
          const ownerIds = map(groupedData[k], 'owner_id');

          let amount = 0;
          let average = 0;
          if (isForecast) {
            amount = reduce(groupedData[k], (t, item) => (t + (item.stages.won.amount || 0)), 0); // only care about won
            const totalForecast = reduce(groupedData[k], (t, item) => (t + (item[forecastKey] || 0)), 0);
            average = y !== 0 ? (totalForecast * 100) / y : 0;
          } else {
            amount = sumBy(groupedData[k], 'amount');
          }

          data.push({
            y,
            salesReps: numeral(y).format('0'),
            amount: formatMoney(amount, true, 1, symbol, suffix),
            count: numeral(count).format('0'),
            average: numeral(average).format('0.0'),
            ownerIds,
            events: {
              click: (event) => {
                const { period, forecastCategoryParams } = this.state;
                this.props.onChartSegmentSelect(event.point.options.ownerIds, period, forecastCategoryParams);
              },
            },
          });
        } else {
          data.push({
            y: 0,
            salesReps: '0',
            count: '0',
            amount: '$0',
            average: 0,
          });
        }
      });
      let altName = key;
      let countLabel = `${key} count`;
      let amountLabel = `Amount ${key}`;


      if (key === 'won') {
        altName = 'Win Rate';
        countLabel = 'Won count';
        amountLabel = 'Amount won';
      } else if (key === 'lost') {
        altName = 'Loss Rate';
        countLabel = 'Loss count';
        amountLabel = 'Amount lost';
      } else if (key === 'noDecision') {
        altName = 'No Decision Rate';
        countLabel = 'No Decision count';
        amountLabel = 'Amount lost';
      } else if (isForecast) {
        altName = 'Forecast Accuracy';
        countLabel = 'count';
        amountLabel = 'Amount won';
      }
      series.push({
        name: isForecast ? 'Forecast Accuracy' : getNameFromKey(key),
        altName,
        countLabel,
        amountLabel,
        color: isForecast ? '#0c7cd5' : getColorFromKey(key),
        mean: accounting.formatNumber(buckets[key].mean[type], 1),
        median: accounting.formatNumber(buckets[key].median[type], 1),
        pointWidth: 10,
        data,
        isForecast,
        fillOpacity: 0.05,
        ...(isForecast ? {
          marker: {
            fillColor: '#ffffff',
            lineWidth: 2,
            radius: isFontSizeBig ? 6 : 5,
            lineColor: null, // inherit from series
          },
          type: 'areaspline',
        } : {
          marker: {
            symbol: 'circle',
            radius: isFontSizeBig ? 6 : 5,
          },
        }),
      });
    });


    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        type: chartType === 'line' ? 'areaspline' : chartType,
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      tooltip: {
        /*
        headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
        pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}:&nbsp;</td><td style="padding:0"> <b>{point.y:.0f}</b></td></tr>',
        footerFormat: '</table>',
        */
        formatter: function fmt() {
          const { isForecast } = this.series.options;
          const header = `<thead>
            <tr>
              <th colspan="2" style="color: ${this.series.color}; font-weight: bold;">
                ${this.series.options.altName} ${this.key.altLabel}
              </th>
            </tr>
          </thead>`;

          let body = `
            <tr>
              <td>Number of reps:</td>
              <td class="text-right">${this.point.options.salesReps}</td>
            </tr>
          `;
          if (isForecast) {
            body += `
              <tr>
                <td>Avg Forecast Accuracy:</td>
                <td class="text-right">${this.point.options.average}%</td>
              </tr>
            `;
          }

          body += `
            <tr>
              <td>${this.series.options.amountLabel}:</td>
              <td class="text-right">${this.point.options.amount}</td>
            </tr>
          `;

          if (!isForecast) {
            body += `
              <tr>
                <td>${this.series.options.countLabel}:</td>
                <td class="text-right">${this.point.options.count}</td>
              </tr>
            `;
          }

          return `<table class="table table-condensed" style="${isFontSizeBig ? 'font-size: 16px' : 'font-size: 12px'}; margin: 0;">${header}<tbody>${body}</tbody></table>`;
        },
        useHTML: true,
        backgroundColor: 'rgba(0,0,0, 0.75)',
        borderColor: 'rgba(0,0,0, 0.95)',
        style: {
          color: 'rgba(255, 255, 255, 0.75)',
        },
      },
      legend: {
        itemStyle: {
          color: 'rgba(0, 0, 0, 0.75)',
          fontSize: isFontSizeBig ? '16px' : null,
        },
        // itemWidth: 200,

        itemHiddenStyle: {
          color: 'rgba(0, 0, 0, 0.2)',
        },
        itemHoverStyle: {
          color: 'rgba(0, 0, 0, 1)',
        },
        useHTML: true,
        labelFormatter: function formatter() {
          const { mean, median, altName } = this.userOptions;
          return `${altName}<br /><span style="font-weight: 400; opacity: .8;"><nobr>Avg: ${mean}%</nobr><br /><nobr>Median: ${median}%</nobr></span>`;
        },
      },
      xAxis: {
        categories,
        alternateGridColor: 'rgba(66,66,66,0.1)',
        crosshair: {
          width: 1,
        },
        lineWidth: 0,
        labels: {
          style: {
            color: 'rgba(0, 0, 0, .5)',
            fontSize: isFontSizeBig ? '16px' : null,
          },
          formatter: function fx() {
            return this.value.label;
          },
        },
        tickColor: 'rgba(66,66,66,0.5)',
      },
      yAxis: {
        title: {
          text: 'Active Reps',
          style: {
            color: 'rgba(0, 0, 0, 0.8)',
            fontSize: isFontSizeBig ? '16px' : null,
            fontWeight: 'bold',
          },
        },
        labels: {
          style: {
            color: 'rgba(0, 0, 0, .5)',
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
        gridLineColor: 'rgba(66,66,66,0.5)',
        tickColor: 'rgba(66,66,66,0.5)',
      },
      series,
      plotOptions: {
        column: {
          borderWidth: 0,
        },
      },
    };
    return (
      <div className="animated fadeIn">
        <ReactHighcharts config={config} />
      </div>
    );
  }

  renderMenu() {
    const { forecastClosedMonths } = this.props;
    const { type, chartType } = this.state;

    return (
      <h5 className="sub-header-with-controls">
        <Dropdown
          id="distribution-chart-dropdown"
        >
          <Dropdown.Toggle bsSize="small">
            {chartType} Chart
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <MenuItem
              active={chartType === 'line'}
              onSelect={() => { this.setChartType('line'); }}
            >
              Line Chart
            </MenuItem>
            <MenuItem
              active={chartType === 'column'}
              onSelect={() => { this.setChartType('column'); }}
            >
              Column Chart
            </MenuItem>
          </Dropdown.Menu>
        </Dropdown>
        &nbsp;
        <span>of outcome rates by opportunity</span>
        &nbsp;
        <Dropdown
          id="distribution-count-dropdown"
        >
          <Dropdown.Toggle bsSize="small">
            {type}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <MenuItem
              active={type === 'amount'}
              onSelect={() => { this.setReportType('amount'); }}
            >
              Amount
            </MenuItem>
            <MenuItem
              active={type === 'count'}
              onSelect={() => { this.setReportType('count'); }}
            >
              Count
            </MenuItem>
          </Dropdown.Menu>
        </Dropdown>
        {false && <ForecastForm
          disabled={this.state.loadingForecast}
          userPreferenceKey={USER_PREFERENCE_FORECAST_CATEGORY}
          userPreferencePeriodKey={USER_PREFERENCE_PERIOD}
          rememberControlStates
          forecastClosedMonths={forecastClosedMonths}
          onParamChange={this.handleForecastParamChange}
        />}
      </h5>
    );
  }

  render() {
    const { displayForecastData } = this.props;
    const { loadingForecast } = this.state;
    const loading = displayForecastData && loadingForecast;
    return (
      <div className="animated fadeIn">
        <div>
          {this.renderMenu()}
          {!loading && this.renderGraph()}
          {loading && <div><ProgressBar /></div>}
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    outcome: getOutcome(state),
    hasNoDecisionSetup: getHasNoDecisionSetup(state),
    organization: getUserCurrentOrganization(state),
    isFontSizeBig: getIsFontSizeBig(state),
  };
}

DistributionGraph.propTypes = {
  dispatch: PropTypes.func.isRequired,
  outcome: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hasNoDecisionSetup: PropTypes.bool.isRequired,
  organization: PropTypes.shape({}).isRequired,
  forecastClosedMonths: PropTypes.number.isRequired,
  minForecastDate: PropTypes.string,
  maxForecastDate: PropTypes.string,
  criteria: PropTypes.shape({}),
  onChartSegmentSelect: PropTypes.func,
  displayForecastData: PropTypes.bool,
  isFontSizeBig: PropTypes.bool.isRequired,
  forecastKey: PropTypes.string,
};
DistributionGraph.defaultProps = {
  criteria: null,
  minForecastDate: null,
  maxForecastDate: null,
  onChartSegmentSelect: () => {},
  displayForecastData: false,
  forecastKey: 'forecast',
};

DistributionGraph.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.object.isRequired,
};
export default connect(mapStateToProps)(DistributionGraph);
